import React, { useState, useEffect } from "react";
import { Map } from "pigeon-maps";
import {
  BASEMAPS,
  BasemapId,
  getBasemapProvider,
  resolveBasemapId,
  DEFAULT_BASEMAP_ID,
} from "../../data/basemaps";

const STORAGE_KEY = "mapBasemap";

type MapStyleSwitcherProps = {
  onBasemapChange: (id: BasemapId) => void;
  /** @deprecated kept for older call sites — aliased to onBasemapChange */
  onStyleChange?: (id: string) => void;
};

const MapStyleSwitcher: React.FC<MapStyleSwitcherProps> = ({
  onBasemapChange,
  onStyleChange,
}) => {
  const notify = onBasemapChange || onStyleChange || (() => undefined);
  const [basemapId, setBasemapId] = useState<BasemapId>(DEFAULT_BASEMAP_ID);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const saved =
      localStorage.getItem(STORAGE_KEY) || localStorage.getItem("mapStyle");
    const id = resolveBasemapId(saved);
    setBasemapId(id);
    notify(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem(STORAGE_KEY) || localStorage.getItem("mapStyle")) {
        return;
      }
      const id: BasemapId = e.matches ? "night" : "standard";
      setBasemapId(id);
      notify(id);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [notify]);

  const handleStyleChange = (id: BasemapId) => {
    setBasemapId(id);
    localStorage.setItem(STORAGE_KEY, id);
    notify(id);
    setIsDropdownOpen(false);
  };

  return (
    <div className="map-style-switcher">
      <button
        type="button"
        onClick={() => setIsDropdownOpen((o) => !o)}
        className="map-style-toggle"
      >
        Change Map
      </button>

      {isDropdownOpen && (
        <div className="map-style-dropdown">
          {BASEMAPS.map((basemap) => (
            <div
              key={basemap.id}
              className={`map-style-option ${
                basemapId === basemap.id ? "selected" : ""
              }`}
              onClick={() => handleStyleChange(basemap.id)}
            >
              <div className="map-style-preview">
                <Map
                  provider={getBasemapProvider(basemap.id)}
                  center={[50, 14]}
                  zoom={4}
                  dprs={[1, 2]}
                  mouseEvents={false}
                  touchEvents={false}
                />
              </div>
              <span className="mapLabel">{basemap.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapStyleSwitcher;
